<template>
  <div class="self-test-container">
    <!-- 测试选择页面 -->
    <div v-if="currentStep === 'select'" class="test-select">
      <div class="select-header">
        <h2>自主测试</h2>
        <p>选择测试类型和难度,开始您的单词测试之旅</p>
      </div>

      <div class="test-options">
        <div class="option-group">
          <h3>测试类型</h3>
          <el-checkbox-group v-model="testConfig.testTypes">
            <el-checkbox label="en_to_cn">英译中</el-checkbox>
            <el-checkbox label="cn_to_en">中译英</el-checkbox>
            <el-checkbox label="listening_choice">听力选择</el-checkbox>
            <el-checkbox label="listening_dictation">听力听写</el-checkbox>
          </el-checkbox-group>
          <div class="option-tip">可选择多种测试类型进行综合测试</div>
        </div>

        <div class="option-group">
          <h3>难度等级</h3>
          <el-radio-group v-model="testConfig.difficulty">
            <el-radio label="easy">简单</el-radio>
            <el-radio label="medium">中等</el-radio>
            <el-radio label="hard">困难</el-radio>
          </el-radio-group>
        </div>

        <div class="option-group">
          <h3>题目数量</h3>
          <el-radio-group v-model="testConfig.limit">
            <el-radio :label="5">5题</el-radio>
            <el-radio :label="10">10题</el-radio>
            <el-radio :label="20">20题</el-radio>
            <el-radio :label="30">30题</el-radio>
          </el-radio-group>
        </div>
      </div>

      <div class="start-button">
        <el-button 
          type="primary" 
          size="large" 
          @click="startTest"
          :loading="loading"
        >
          开始测试
        </el-button>
      </div>
    </div>

    <!-- 测试进行页面 -->
    <div v-if="currentStep === 'testing'" class="test-progress">
      <div class="progress-header">
        <div class="progress-info">
          <span>共 {{ questions.length }} 题</span>
          <span>用时: {{ formatTime(testTime) }}</span>
          <span>已答: {{ answeredCount }}/{{ questions.length }}</span>
        </div>
        <div class="progress-bar">
          <el-progress :percentage="progressPercentage" :show-text="false"></el-progress>
        </div>
      </div>

      <div class="questions-container">
        <div v-for="(question, index) in questions" :key="index" class="question-item">
          <div class="question-header">
            <span class="question-number">第 {{ index + 1 }} 题</span>
            <span class="question-type">{{ getQuestionTypeName(question.question_type) }}</span>
            <span :class="['answer-status', getAnswerStatus(index)]">
              {{ getAnswerStatusText(index) }}
            </span>
          </div>
          
          <div class="question-content">
            <!-- 英译中题目 -->
            <div v-if="question.question_type === 'en_to_cn'" class="question-type-en-cn">
              <div class="question-text">
                <h4>{{ question.question_content }}</h4>
              </div>
              <div class="options">
                <el-radio-group v-model="userAnswers[index]" @change="updateAnswerStatus">
                  <el-radio 
                    v-for="(option, optIndex) in question.options" 
                    :key="optIndex" 
                    :label="option"
                    class="option-item"
                  >
                    {{ option }}
                  </el-radio>
                </el-radio-group>
              </div>
            </div>

            <!-- 中译英题目 -->
            <div v-if="question.question_type === 'cn_to_en'" class="question-type-cn-en">
              <div class="question-text">
                <h4>{{ question.question_content }}</h4>
              </div>
              <div class="options">
                <el-radio-group v-model="userAnswers[index]" @change="updateAnswerStatus">
                  <el-radio 
                    v-for="(option, optIndex) in question.options" 
                    :key="optIndex" 
                    :label="option"
                    class="option-item"
                  >
                    {{ option }}
                  </el-radio>
                </el-radio-group>
              </div>
            </div>

            <!-- 听力选择题 -->
            <div v-if="question.question_type === 'listening_choice'" class="question-type-listening">
              <div class="audio-section">
                <el-button 
                  type="primary" 
                  @click="playAudio(question)"
                  :loading="audioLoading"
                  icon="el-icon-video-play"
                >
                  播放音频
                </el-button>
              </div>
              <div class="options">
                <el-radio-group v-model="userAnswers[index]" @change="updateAnswerStatus">
                  <el-radio 
                    v-for="(option, optIndex) in question.options" 
                    :key="optIndex" 
                    :label="option"
                    class="option-item"
                  >
                    {{ option }}
                  </el-radio>
                </el-radio-group>
              </div>
            </div>

            <!-- 听力听写题 -->
            <div v-if="question.question_type === 'listening_dictation'" class="question-type-dictation">
              <div class="audio-section">
                <el-button 
                  type="primary" 
                  @click="playAudio(question)"
                  :loading="audioLoading"
                  icon="el-icon-video-play"
                >
                  播放音频
                </el-button>
              </div>
              <div class="dictation-input">
                <el-input
                  v-model="userAnswers[index]"
                  placeholder="请输入您听到的内容"
                  type="textarea"
                  :rows="3"
                  @input="updateAnswerStatus"
                ></el-input>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="submit-section">
        <el-button 
          type="primary" 
          size="large" 
          @click="finishTest"
          :disabled="answeredCount === 0"
          :loading="submitting"
        >
          提交测试 ({{ answeredCount }}/{{ questions.length }})
        </el-button>
      </div>
    </div>

    <!-- 测试结果页面 -->
    <div v-if="currentStep === 'result'" class="test-result">
      <div class="result-header">
        <h2>测试完成！</h2>
        <div class="score-display">
          <div class="score-circle">
            <span class="score-number">{{ testResult.accuracy }}%</span>
            <span class="score-label">正确率</span>
          </div>
        </div>
      </div>

      <div class="result-summary">
        <div class="summary-cards">
          <div class="summary-card correct">
            <div class="card-icon">✓</div>
            <div class="card-content">
              <div class="card-number">{{ testResult.correctAnswers }}</div>
              <div class="card-label">答对题数</div>
            </div>
          </div>
          <div class="summary-card wrong">
            <div class="card-icon">✗</div>
            <div class="card-content">
              <div class="card-number">{{ testResult.wrongAnswers }}</div>
              <div class="card-label">答错题数</div>
            </div>
          </div>
          <div class="summary-card time">
            <div class="card-icon">⏱</div>
            <div class="card-content">
              <div class="card-number">{{ formatTime(testResult.totalTime) }}</div>
              <div class="card-label">用时</div>
            </div>
          </div>
        </div>
      </div>

      <div class="result-actions">
        <el-button @click="restartTest">重新测试</el-button>
        <el-button type="primary" @click="viewDetails">查看详情</el-button>
        <el-button @click="goHome">返回首页</el-button>
      </div>
    </div>

    <!-- 题目详情弹窗 -->
    <el-dialog title="测试详情" :visible.sync="showDetails" width="80%">
      <div class="question-details">
        <div v-for="(question, index) in questions" :key="index" class="detail-question">
          <div class="question-header">
            <span class="question-number">第 {{ index + 1 }} 题</span>
            <span class="question-type">{{ getQuestionTypeName(question.question_type) }}</span>
            <span :class="['answer-status', getAnswerStatus(index)]">
              {{ getAnswerStatusText(index) }}
            </span>
          </div>
          <div class="question-content">
            <p><strong>题目:</strong> {{ question.question_content }}</p>
            <p><strong>正确答案:</strong> {{ question.correct_answer }}</p>
            <p><strong>您的答案:</strong> {{ userAnswers[index] || '未作答' }}</p>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import SelfTestAPI from "@/api/selfTest";

export default {
  name: "SelfTest",
  data() {
    return {
      currentStep: 'select', // select, testing, result
      loading: false,
      audioLoading: false,
      
      // 测试配置
      testConfig: {
        testTypes: ['en_to_cn'], // 默认选择英译中
        difficulty: 'easy',
        limit: 10
      },
      
      // 测试数据
      questions: [],
      userAnswers: [],
      testStartTime: null,
      testTime: 0,
      testTimer: null,
      
      // 测试结果
      testResult: {},
      showDetails: false
    };
  },
  
  computed: {
    answeredCount() {
      return this.userAnswers.filter(answer => answer && answer.trim() !== '').length;
    },
    
    progressPercentage() {
      return Math.round((this.answeredCount / this.questions.length) * 100);
    }
  },
  
  methods: {
    // 开始测试
    async startTest() {
      // 检查是否选择了测试类型
      if (!this.testConfig.testTypes || this.testConfig.testTypes.length === 0) {
        this.$message.warning('请至少选择一种测试类型');
        return;
      }
      
      this.loading = true;
      try {
        const response = await SelfTestAPI.getQuestions(this.testConfig);
        if (response.success) {
          this.questions = response.data;
          this.userAnswers = new Array(this.questions.length).fill('');
          this.currentStep = 'testing';
          this.testStartTime = Date.now();
          this.startTimer();
        } else {
          this.$message.error(response.message);
        }
      } catch (error) {
        this.$message.error('获取题目失败');
      } finally {
        this.loading = false;
      }
    },
    
    // 开始计时
    startTimer() {
      this.testTimer = setInterval(() => {
        this.testTime = Math.floor((Date.now() - this.testStartTime) / 1000);
      }, 1000);
    },
    
    // 停止计时
    stopTimer() {
      if (this.testTimer) {
        clearInterval(this.testTimer);
        this.testTimer = null;
      }
    },
    
    // 播放音频
    playAudio(question) {
      if (question.audio_url) {
        this.audioLoading = true;
        const audio = new Audio(question.audio_url);
        audio.onended = () => {
          this.audioLoading = false;
        };
        audio.onerror = () => {
          this.audioLoading = false;
          this.$message.error('音频播放失败');
        };
        audio.play();
      } else {
        this.$message.warning('暂无音频文件');
      }
    },
    
    // 更新答案状态
    updateAnswerStatus() {
      // 答案状态会自动更新，这里可以添加其他逻辑
    },
    
    // 完成测试
    async finishTest() {
      this.submitting = true;
      this.stopTimer();
      
      // 计算结果
      let correctAnswers = 0;
      let wrongAnswers = 0;
      
      this.questions.forEach((question, index) => {
        const userAnswer = this.userAnswers[index];
        
        // 更灵活的答案比较逻辑
        let isCorrect = false;
        
        if (question.question_type === 'listening_dictation') {
          // 听写题：直接比较文本内容（忽略大小写和空格）
          isCorrect = userAnswer && userAnswer.trim().toLowerCase() === question.correct_answer.trim().toLowerCase();
        } else {
          // 选择题：比较选项内容
          if (question.options && question.options.length > 0) {
            // 如果correct_answer是选项索引（如"A", "B", "C", "D"）
            if (question.correct_answer.match(/^[A-D]$/)) {
              const correctIndex = question.correct_answer.charCodeAt(0) - 65; // A=0, B=1, C=2, D=3
              isCorrect = userAnswer === question.options[correctIndex];
            } else {
              // 如果correct_answer是选项内容
              isCorrect = userAnswer === question.correct_answer;
            }
          } else {
            // 没有选项的情况，直接比较
            isCorrect = userAnswer === question.correct_answer;
          }
        }
        
        if (isCorrect) {
          correctAnswers++;
        } else {
          wrongAnswers++;
        }
      });
      
      this.testResult = {
        testTypes: this.testConfig.testTypes,
        difficulty: this.testConfig.difficulty,
        totalQuestions: this.questions.length,
        correctAnswers,
        wrongAnswers,
        accuracy: Math.round((correctAnswers / this.questions.length) * 100),
        totalTime: this.testTime,
        testResult: this.questions.map((question, index) => {
          const userAnswer = this.userAnswers[index];
          let isCorrect = false;
          
          // 使用相同的判断逻辑
          if (question.question_type === 'listening_dictation') {
            isCorrect = userAnswer && userAnswer.trim().toLowerCase() === question.correct_answer.trim().toLowerCase();
          } else {
            if (question.options && question.options.length > 0) {
              if (question.correct_answer.match(/^[A-D]$/)) {
                const correctIndex = question.correct_answer.charCodeAt(0) - 65;
                isCorrect = userAnswer === question.options[correctIndex];
              } else {
                isCorrect = userAnswer === question.correct_answer;
              }
            } else {
              isCorrect = userAnswer === question.correct_answer;
            }
          }
          
          return {
            question_id: question.id,
            user_answer: userAnswer,
            correct_answer: question.correct_answer,
            is_correct: isCorrect
          };
        })
      };
      
      // 提交结果
      try {
        await SelfTestAPI.submitTest(this.testResult);
        this.$message.success('测试结果已保存');
      } catch (error) {
        console.error('提交测试结果失败:', error);
        this.$message.warning('测试结果保存失败，但可以查看结果');
      }
      
      this.currentStep = 'result';
      this.submitting = false;
    },
    
    // 重新测试
    restartTest() {
      this.currentStep = 'select';
      this.questions = [];
      this.userAnswers = [];
      this.testTime = 0;
      this.testResult = {};
    },
    
    // 查看详情
    viewDetails() {
      this.showDetails = true;
    },
    
    // 返回首页
    goHome() {
      this.$router.push('/home');
    },
    
    // 格式化时间
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    },
    
    // 获取测试类型名称
    getQuestionTypeName(type) {
      const types = {
        'en_to_cn': '英译中',
        'cn_to_en': '中译英',
        'listening_choice': '听力选择',
        'listening_dictation': '听力听写'
      };
      return types[type] || type;
    },
    
    // 获取答案状态
    getAnswerStatus(index) {
      const question = this.questions[index];
      const userAnswer = this.userAnswers[index];
      if (!userAnswer) return 'unanswered';
      
      // 使用与finishTest相同的判断逻辑
      let isCorrect = false;
      
      if (question.question_type === 'listening_dictation') {
        isCorrect = userAnswer && userAnswer.trim().toLowerCase() === question.correct_answer.trim().toLowerCase();
      } else {
        if (question.options && question.options.length > 0) {
          if (question.correct_answer.match(/^[A-D]$/)) {
            const correctIndex = question.correct_answer.charCodeAt(0) - 65;
            isCorrect = userAnswer === question.options[correctIndex];
          } else {
            isCorrect = userAnswer === question.correct_answer;
          }
        } else {
          isCorrect = userAnswer === question.correct_answer;
        }
      }
      
      return isCorrect ? 'correct' : 'wrong';
    },
    
    // 获取答案状态文本
    getAnswerStatusText(index) {
      const status = this.getAnswerStatus(index);
      const statusTexts = {
        'correct': '正确',
        'wrong': '错误',
        'unanswered': '未作答'
      };
      return statusTexts[status];
    }
  },
  
  beforeDestroy() {
    this.stopTimer();
  }
};
</script>

<style scoped>
.self-test-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
}

/* 测试选择页面 */
.test-select {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
}

.select-header {
  text-align: center;
  margin-bottom: 40px;
}

.select-header h2 {
  color: #333;
  font-size: 32px;
  margin-bottom: 10px;
}

.select-header p {
  color: #666;
  font-size: 16px;
}

.test-options {
  margin-bottom: 40px;
}

.option-group {
  margin-bottom: 30px;
}

.option-group h3 {
  color: #333;
  margin-bottom: 15px;
  font-size: 18px;
}

.option-tip {
  color: #666;
  font-size: 14px;
  margin-top: 10px;
  font-style: italic;
}

.start-button {
  text-align: center;
}

/* 测试进行页面 */
.test-progress {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
}

.questions-container {
  margin-bottom: 30px;
}

.question-item {
  margin-bottom: 30px;
  padding: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 15px;
  background: #fafafa;
}

.question-item:last-child {
  margin-bottom: 0;
}

.submit-section {
  text-align: center;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #f0f0f0;
}

.progress-header {
  margin-bottom: 30px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
  color: #666;
  font-size: 14px;
}


.question-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #f0f0f0;
}

.question-number {
  background: #409eff;
  color: white;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
  margin-right: 15px;
}

.question-type {
  background: #f0f0f0;
  color: #666;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 14px;
}

.answer-status {
  margin-left: auto;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.answer-status.correct {
  background: #f0f9ff;
  color: #409eff;
}

.answer-status.wrong {
  background: #fef2f2;
  color: #f56c6c;
}

.answer-status.unanswered {
  background: #f4f4f5;
  color: #909399;
}

.question-content {
  margin-bottom: 20px;
}

.question-text h4 {
  font-size: 20px;
  color: #333;
  margin-bottom: 20px;
  text-align: center;
}

.options {
  margin-top: 15px;
}

.option-item {
  display: block;
  margin-bottom: 10px;
  padding: 12px;
  border: 2px solid #f0f0f0;
  border-radius: 8px;
  transition: all 0.3s;
}

.option-item:hover {
  border-color: #409eff;
  background: #f8f9ff;
}

.audio-section {
  text-align: center;
  margin-bottom: 20px;
}

.dictation-input {
  margin-top: 15px;
}

/* 测试结果页面 */
.test-result {
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.1);
}

.result-header {
  text-align: center;
  margin-bottom: 40px;
}

.result-header h2 {
  color: #333;
  font-size: 32px;
  margin-bottom: 20px;
}

.score-display {
  display: flex;
  justify-content: center;
}

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
}

.score-number {
  font-size: 32px;
  font-weight: bold;
}

.score-label {
  font-size: 14px;
}

.result-summary {
  margin-bottom: 40px;
}

.summary-cards {
  display: flex;
  justify-content: space-around;
  margin-bottom: 30px;
}

.summary-card {
  display: flex;
  align-items: center;
  padding: 20px;
  border-radius: 15px;
  background: #f8f9ff;
}

.summary-card.correct {
  background: #f0f9ff;
}

.summary-card.wrong {
  background: #fef2f2;
}

.summary-card.time {
  background: #f0fdf4;
}

.card-icon {
  font-size: 24px;
  margin-right: 15px;
}

.card-content {
  text-align: center;
}

.card-number {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.card-label {
  font-size: 14px;
  color: #666;
}

.result-actions {
  text-align: center;
}

.result-actions .el-button {
  margin: 0 10px;
}

/* 题目详情 */
.question-details {
  max-height: 500px;
  overflow-y: auto;
}

.detail-question {
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.detail-question .question-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}

.answer-status {
  margin-left: auto;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
}

.answer-status.correct {
  background: #f0f9ff;
  color: #409eff;
}

.answer-status.wrong {
  background: #fef2f2;
  color: #f56c6c;
}

.answer-status.unanswered {
  background: #f4f4f5;
  color: #909399;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .self-test-container {
    padding: 10px;
  }
  
  .test-select,
  .test-progress,
  .test-result {
    padding: 20px;
  }
  
  .summary-cards {
    flex-direction: column;
    gap: 15px;
  }
  
  .question-actions {
    flex-direction: column;
    gap: 10px;
  }
}
</style>